import { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { ConditionalTooltip } from "../../../../../../../components";
import ModalWrapper from "../../../../../../../components/ModalWrapper";
import { NotificationsLevel } from "../../../../../../../lib/enums";
import { show as showNotification } from "../../../../../../../notifications/notifications";
import useLLMSummaryFeedback from "../../../../../hooks/useLLMSummaryFeedback";
import styles from "../llm-summary.module.scss";

interface Props {
  closeModal: () => void;
}

const MAX_FEEDBACK_LENGTH = 10000;
const TOOLTIP_MESSAGE = "Feedback must be less than 10000 characters.";

export const LLMSummaryFeedbackModal = ({ closeModal }: Props) => {
  const [selected, setSelected] = useState<"like" | "dislike" | null>(null);
  const [feedback, setFeedback] = useState("");

  const { postLLMSummaryFeedback } = useLLMSummaryFeedback();

  const handleSubmit = () => {
    postLLMSummaryFeedback({
      isPositive: selected === "like",
      comment: feedback
    })
      .then(() => {
        showNotification({
          level: NotificationsLevel.SUCCESS,
          message: "Thank you for your feedback!"
        });
        closeModal();
      })
      .catch(() => {
        showNotification({
          level: NotificationsLevel.ERROR,
          message: "Error while submitting feedback. Please try again."
        });
      });
  };

  const isDisabled = !selected || feedback.length >= MAX_FEEDBACK_LENGTH;

  return (
    <ModalWrapper
      show
      title="Leave Feedback"
      size={"lg"}
      onHide={closeModal}
      className="llm-summary-feedback-modal"
    >
      <div className={styles.llm_summary_feedback}>
        <Form.Group className={"width-100-pc"}>
          <Form.Label className={"form-text--important"}>
            Did you find the AI Summary helpful?
          </Form.Label>
          <div className={styles.llm_summary_feedback__buttons}>
            <Button
              variant={selected === "like" ? "primary" : "outline-primary"}
              className={styles.llm_summary_feedback__left_button}
              onClick={() => setSelected("like")}
            >
              Yes!
            </Button>
            <Button
              variant={selected === "dislike" ? "primary" : "outline-primary"}
              className={styles.llm_summary_feedback__right_button}
              onClick={() => setSelected("dislike")}
            >
              No.
            </Button>
          </div>
        </Form.Group>

        <Form.Group className={"width-100-pc"}>
          <Form.Label className={"form-text--important"}>
            Leave us your feedback (optional)
          </Form.Label>
          <textarea
            className={"width-100-pc"}
            name={"feedback"}
            rows={8}
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
          />
        </Form.Group>
        <div className={styles.llm_summary_feedback__submit_buttons}>
          <Button variant="outline-primary" onClick={closeModal}>
            Cancel
          </Button>
          <ConditionalTooltip
            tooltip={TOOLTIP_MESSAGE}
            condition={isDisabled && !!feedback}
            placement="top"
          >
            <div>
              <Button
                type="submit"
                variant="primary"
                onClick={handleSubmit}
                disabled={isDisabled}
              >
                Submit
              </Button>
            </div>
          </ConditionalTooltip>
        </div>
      </div>
    </ModalWrapper>
  );
};

const LLMSummaryFeedback = () => {
  const [show, setShow] = useState(false);

  return (
    <>
      <div className={styles.llm_summary_refresh_button}>
        <span onClick={() => setShow(true)}>Leave Feedback</span>
      </div>

      {show && <LLMSummaryFeedbackModal closeModal={() => setShow(false)} />}
    </>
  );
};

export default LLMSummaryFeedback;
